import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { questionnaireService } from '../services/questionnaireService';
import { templateService } from '../services/templateService';
import type { QuestionnaireTemplate } from '../types/questionnaires';

interface CreateQuestionnaireModalProps {
  onClose: () => void;
  onCreated: () => void;
}

const CreateQuestionnaireModal: React.FC<CreateQuestionnaireModalProps> = ({ onClose, onCreated }) => {
  const { user, userProfile } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [templates, setTemplates] = useState<QuestionnaireTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<QuestionnaireTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await templateService.listTemplates();
      setTemplates(data);
      if (data.length > 0) {
        setSelectedTemplate(data[0]); // Auto-select first template
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      alert('Bitte geben Sie einen Titel ein');
      return;
    }
    
    if (!selectedTemplate) {
      alert('Bitte wählen Sie eine Vorlage aus');
      return;
    }
    
    if (!user || !userProfile) return;
    
    try {
      setIsCreating(true);
      
      const questionnaireData = {
        title: title.trim(),
        description: description.trim() || undefined,
        templateId: selectedTemplate.id,
        templateName: selectedTemplate.name,
        createdBy: user.uid,
        createdByName: userProfile.displayName
      };
      
      const id = await questionnaireService.createQuestionnaire(questionnaireData);
      console.log('✅ Questionnaire created:', id);
      
      alert('Fragebogen erfolgreich erstellt!');
      onCreated();
      onClose();
      
    } catch (error) {
      console.error('Error creating questionnaire:', error);
      alert('Fehler beim Erstellen des Fragebogens. Bitte versuchen Sie es erneut.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Neuer Fragebogen</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-semibold text-gray-900 mb-2">
              Titel <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. 'Q4 2024 Projekt-Showcase Sammlung'"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-semibold text-gray-900 mb-2">
              Beschreibung (optional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Beschreiben Sie den Zweck dieses Fragebogens..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Template Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Vorlage <span className="text-red-500">*</span>
            </label>
            
            {isLoading ? (
              <div className="text-gray-500">Lade Vorlagen...</div>
            ) : templates.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
                Keine Vorlagen verfügbar. Bitte erstellen Sie zuerst eine Vorlage.
              </div>
            ) : (
              <div className="space-y-3">
                {templates.map(template => (
                  <div
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={`
                      border-2 rounded-lg p-4 cursor-pointer transition-all
                      ${selectedTemplate?.id === template.id
                        ? 'border-purple-600 bg-purple-50' 
                        : 'border-gray-200 hover:border-purple-300'
                      }
                    `}
                  >
                    <div className="flex items-start">
                      <input
                        type="radio"
                        name="template"
                        checked={selectedTemplate?.id === template.id}
                        onChange={() => setSelectedTemplate(template)}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {template.name}
                        </h4>
                        {template.description && (
                          <p className="text-sm text-gray-600 mb-2">
                            {template.description}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          {template.fields.length} Felder
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleCreate}
              disabled={isCreating || !title.trim() || !selectedTemplate}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Erstelle...' : 'Fragebogen erstellen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateQuestionnaireModal;
